import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "ui-button inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors enabled:active:brightness-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-control-focus)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--color-signal)] text-white hover:bg-[var(--color-signal-hover)] shadow-lg shadow-[var(--color-signal)]/20",
        secondary:
          "bg-[var(--color-control-surface)] text-[var(--color-text)] border border-[var(--color-control-border)] hover:border-[var(--color-signal-hover)] hover:bg-[var(--color-signal-muted)]",
        ghost:
          "border border-[var(--color-control-border)] bg-transparent text-[var(--color-text)] hover:bg-[var(--color-signal-muted)]",
        danger:
          "bg-[var(--color-error)] text-white hover:bg-[#e63535] shadow-lg shadow-[var(--color-error)]/20",
        accent:
          "bg-[var(--color-accent)] text-[var(--color-ink)] hover:bg-[#a8f02a] shadow-lg shadow-[var(--color-accent)]/20",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-2",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        data-icon-only={size === "icon" || undefined}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
